/**
 * Debug Logger that stores logs in localStorage for debugging
 * Useful when redirects happen too fast to see console logs
 */

interface LogEntry {
  timestamp: string;
  level: 'log' | 'info' | 'warn' | 'error';
  message: string;
  data?: any;
}

const MAX_LOGS = 500; // Keep last 500 log entries
const STORAGE_KEY = 'kavithai_debug_logs';

class DebugLogger {
  private logs: LogEntry[] = [];
  private originalConsole: {
    log: typeof console.log;
    info: typeof console.info;
    warn: typeof console.warn;
    error: typeof console.error;
  };
  private enabled: boolean;

  constructor() {
    this.enabled = import.meta.env.VITE_DEBUG_LOGS === 'true';
    // Save original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    };

    if (!this.enabled) {
      return;
    }

    // Load existing logs from localStorage
    this.loadLogs();

    // Override console methods
    this.setupConsoleOverrides();
  }

  private loadLogs() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load debug logs from localStorage', e);
    }
  }

  private saveLogs() {
    try {
      // Keep only last MAX_LOGS entries
      if (this.logs.length > MAX_LOGS) {
        this.logs = this.logs.slice(-MAX_LOGS);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs));
    } catch (e) {
      console.error('Failed to save debug logs to localStorage', e);
    }
  }

  private addLog(level: LogEntry['level'], message: string, ...args: any[]) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data: args.length > 0 ? args : undefined,
    };

    this.logs.push(entry);
    this.saveLogs();

    // Also log to original console
    this.originalConsole[level](message, ...args);
  }

  private setupConsoleOverrides() {
    // Override console.log
    console.log = (...args: any[]) => {
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      this.addLog('log', message, ...args);
    };

    // Override console.info
    console.info = (...args: any[]) => {
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      this.addLog('info', message, ...args);
    };

    // Override console.warn
    console.warn = (...args: any[]) => {
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      this.addLog('warn', message, ...args);
    };

    // Override console.error
    console.error = (...args: any[]) => {
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      this.addLog('error', message, ...args);
    };
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    this.saveLogs();
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  downloadLogs() {
    const logsJson = this.exportLogs();
    const blob = new Blob([logsJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kavithai-debug-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Create singleton instance
export const debugLogger = new DebugLogger();

// Export for global access
if (typeof window !== 'undefined') {
  (window as any).debugLogger = debugLogger;
}
